import { FC } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';

type ColorName = 'blue' | 'green' | 'orange' | 'purple' | 'red';

interface Props {
    title: string;
    value: string | number;
    subtitle?: string;
    changePercent?: number | null;
    changeLabel?: string;
    variant?: 'default' | 'gradient' | 'border';
    color?: ColorName;
}

const GRADIENTS: Record<ColorName, string> = {
    blue: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    green: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    orange: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    purple: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    red: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
};

const BORDER_COLORS: Record<ColorName, string> = {
    blue: '#3b82f6',
    green: '#22c55e',
    orange: '#f59e0b',
    purple: '#8b5cf6',
    red: '#ef4444',
};

const VALUE_COLORS: Record<ColorName, string> = {
    blue: '#2563eb',
    green: '#16a34a',
    orange: '#d97706',
    purple: '#7c3aed',
    red: '#dc2626',
};

function ChangeIndicator({ value, label }: { value: number; label?: string }) {
    const isPositive = value > 0;
    const color = isPositive ? '#16a34a' : '#dc2626';
    const arrow = isPositive ? '\u25B2' : '\u25BC';
    const text = `${arrow} ${Math.abs(value).toFixed(1)}%`;

    return (
        <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
            {text}
            {label && (
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5, fontWeight: 400 }}>
                    {label}
                </Typography>
            )}
        </Typography>
    );
}

export const KpiCard: FC<Props> = ({
    title,
    value,
    subtitle,
    changePercent,
    changeLabel,
    variant = 'default',
    color = 'blue',
}) => {
    const isGradient = variant === 'gradient';
    const isBorder = variant === 'border';

    return (
        <Card
            sx={{
                height: '100%',
                ...(isGradient && {
                    background: GRADIENTS[color],
                    border: 'none',
                    color: '#fff',
                }),
                ...(isBorder && {
                    borderLeft: `4px solid ${BORDER_COLORS[color]}`,
                }),
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 600,
                        color: isGradient ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.7rem',
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        mt: 0.5,
                        color: isGradient ? '#fff' : VALUE_COLORS[color],
                    }}
                >
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </Typography>
                {subtitle && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: isGradient ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                            display: 'block',
                            mt: 0.25,
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
                {changePercent != null && changePercent !== 0 && (
                    <Box sx={{ mt: 1 }}>
                        {isGradient ? (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                                {changePercent > 0 ? '\u25B2' : '\u25BC'} {Math.abs(changePercent).toFixed(1)}%
                                {changeLabel && (
                                    <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 400, color: 'rgba(255,255,255,0.7)' }}>
                                        {changeLabel}
                                    </Typography>
                                )}
                            </Typography>
                        ) : (
                            <ChangeIndicator value={changePercent} label={changeLabel} />
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
